import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import DataTable from "react-data-table-component";
import {
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import CommonModal from "../../../UiKits/Modals/common/modal";
import {
  DropdownItem as UncontrolledDropdownItem,
  DropdownMenu as UncontrolledDropdownMenu,
  DropdownToggle as UncontrolledDropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { MoreVertical, } from "react-feather";
import { FaDownload, FaRegEye, FaRegStickyNote } from "react-icons/fa";
import { useNavigate } from "react-router";
import axios from "axios";
import { baseURL, orderURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import useRbac from "../../../../Hooks/useRbac";

const getOrderTotal = (row) =>
  row?.is_b2b_order ? row?.b2b_total_inc_gst : row?.order_value;

// ============= TIMEZONE STRATEGY =============
// The person viewing this dashboard could be anywhere (India, Australia, etc).
// "Today" / date-range filters should always mean "today in the VIEWER's own
// timezone" — so we detect it once here and use it everywhere the filter
// dates are converted to/from UTC for the API, and for the date input
// defaults/max/min. This keeps the filter and the displayed dates in sync
// regardless of who is looking at the screen.
//
// The one exception is the SCHEDULED DELIVERY SLOT (scheduledDate /
// scheduledTimeSlot). That represents a real-world delivery window the
// store has committed to in the store's own location (Australia), so it
// stays pinned to "Australia/Sydney" no matter who is viewing — a
// "2PM-4PM" slot always means 2-4pm in Sydney, not 2-4pm wherever the
// viewer happens to be.
const viewerTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const STORE_TZ = "Australia/Sydney";

const DataTableComponent = () => {
  const navigate = useNavigate();
  const { can } = useRbac();
  const userRole = JSON.parse(localStorage.getItem("role_name"));
  const [selectedRows, setSelectedRows] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [toggleDelet, setToggleDelet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, SetShowmodal] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderId, setOrderId] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [showOrderCancellationPopup, setShowOrderCancellationPopup] = useState(false);
  // Default to the VIEWER's local "today", not any fixed timezone's today.
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const isMountedRef = useRef(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);
  const togglePaymentDropdown = () => setPaymentDropdownOpen(prevState => !prevState);
  // "now" drives the live D.TIME calculation. Keep it browser-local so it
  // lines up with the browser-local order timestamps shown in the table.
  const [now, setNow] = useState(moment());
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 60000); // tick every minute
    return () => clearInterval(interval);
  }, []);

  const formik = useFormik({
    initialValues: {
      cancel_reason: "",
    },
    validationSchema: Yup.object({
      cancel_reason: Yup.string().required(
        "Please Enter Reason for Order Cancellation"
      ),
    }),
    onSubmit: async (values) => {
      const token = await JSON.parse(localStorage.getItem("token"));
      let body = {
        order_status: statusValue,
        cancel_reason: formik.values.cancel_reason,
      };
      if (body.order_status === "") {
        return Swal.fire({
          icon: "error",
          title: "Provide Order Status",
        });
      }
      try {
        await axios
          .patch(`${orderURL}/update-order-status/${orderId}`, body, {
            headers: {
              Authorization: `${token}`,
            },
          })
          .then((res) => {
            if (res && res.status === 200) {
              Swal.fire({
                icon: "success",
                title: res?.data?.message,
              });
              setShowOrderCancellationPopup(false);
              setOrderId("");
              setStatusValue("");
              formik.setFieldValue("cancel_reason", "");
              getOrderData();
            }
          });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const exportAllOrdersold = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const userData = JSON.parse(localStorage.getItem("UserData"));

      let params = {
        search_string: searchTerm || "",
        // Convert the filter's calendar date to a UTC range using the
        // VIEWER's own timezone, so "today" means today wherever they are.
        start_date: startDate
          ? moment.tz(startDate, viewerTZ).startOf("day").utc().format()
          : undefined,
        end_date: endDate
          ? moment.tz(endDate, viewerTZ).endOf("day").utc().format()
          : undefined,
        order_status: statusFilter !== "all" ? statusFilter : undefined,
        payment_status: paymentStatusFilter !== "all" ? paymentStatusFilter : undefined,
        export: true,
        page: 1,        // 👈 Fix 1: always start from page 1
        limit: 999999,  // 👈 Fix 1: large limit to bypass pagination
      };

      if (userRole === "store" && userData?._id) {
        params.storeId = userData._id;
      }

      const response = await axios.get(
        `${baseURL}/api/dashboard/get-all-orders-dashboard-export`,
        {
          params,
          headers: { Authorization: token },
        }
      );

      const orders = response?.data?.data?.orders || [];

      if (!orders.length) {
        Swal.fire("No data to export");
        return;
      }

      // 👈 Fix 2: format date as plain text to avoid ###### in Excel
      // Dates are shown in the viewer's local time (plain moment(), no .tz())
      // to match what's shown on-screen and what was used to build the filter.
      const csvData = orders.map((order) => ({
        Order_ID: order.sequence_number,
        Customer: `${order?.users?.first_name || ""} ${order?.users?.last_name || ""}`.trim(),
        Phone: order?.users?.phone || order?.address?.[0]?.addressPhoneNumber || "",
        Date: `="${moment(order.createdAt).format("DD-MM-YYYY HH:mm")}"`, // 👈 Excel text prefix trick
        Total_Amount: getOrderTotal(order), // trade orders: b2b_total_inc_gst; consumer: order_value
        Payment_Status: order.transaction_status,
        Order_Status: order.order_status,
        Store: order?.store?.storeName || "",
        Platform: order.orderSource,
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }); // 👈 BOM for Excel encoding
      saveAs(blob, `orders_${moment().format("YYYYMMDD_HHmm")}.csv`);

    } catch (error) {
      console.error(error);
      Swal.fire("Failed to export orders");
    }
  };

  const exportAllOrders = async () => {
    setIsExporting(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const userData = JSON.parse(localStorage.getItem("UserData"));

      let params = {
        search_string: searchTerm || "",
        start_date: startDate
          ? moment.tz(startDate, viewerTZ).startOf("day").utc().format()
          : undefined,
        end_date: endDate
          ? moment.tz(endDate, viewerTZ).endOf("day").utc().format()
          : undefined,
        order_status: statusFilter !== "all" ? statusFilter : undefined,
        payment_status: paymentStatusFilter !== "all" ? paymentStatusFilter : undefined,
        export: true,
        page: 1,
        limit: 999999,
      };

      if (userRole === "store" && userData?._id) {
        params.storeId = userData._id;
      }

      const response = await axios.get(
        `${baseURL}/api/dashboard/get-all-orders-dashboard-export`,
        { params, headers: { Authorization: token } }
      );

      const orders = response?.data?.data?.orders || [];

      if (!orders.length) {
        Swal.fire("No data to export");
        return;
      }

      const csvData = orders.map((order) => ({
        Order_ID: order.sequence_number,
        Customer: `${order?.users?.first_name || ""} ${order?.users?.last_name || ""}`.trim(),
        Phone: order?.users?.phone || order?.address?.[0]?.addressPhoneNumber || "",
        Date: `="${moment(order.createdAt).format("DD-MM-YYYY HH:mm")}"`,
        Total_Amount: getOrderTotal(order),
        Payment_Status: order.transaction_status,
        Order_Status: order.order_status,
        Store: order?.store?.storeName || "",
        Platform: order.orderSource,
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `orders_${moment().format("YYYYMMDD_HHmm")}.csv`);

    } catch (error) {
      console.error(error);
      Swal.fire("Failed to export orders");
    } finally {
      setIsExporting(false);
    }
  };

  const getOrderData = async () => {
    setIsLoading(true);

    try {
      const token = await JSON.parse(localStorage.getItem("token"));
      const userData = await JSON.parse(localStorage.getItem("UserData"));
      let url =
        userRole === "store"
          ? `${baseURL}/api/dashboard/get-all-orders-dashboard`
          : `${baseURL}/api/dashboard/get-all-orders-dashboard`;

      let params = {
        search_string: searchTerm || '',
        page: currentPage,
        limit: perPage
      };

      if (userRole === "store" && userData?._id) {
        params.storeId = userData._id;
      }

      if (startDate && endDate) {
        params.start_date = moment.tz(startDate, viewerTZ).startOf('day').utc().toISOString();
        params.end_date = moment.tz(endDate, viewerTZ).endOf('day').utc().toISOString();
      }

      if (statusFilter !== 'all') {
        params.order_status = statusFilter;
      }
      if (paymentStatusFilter !== 'all') {
        params.payment_status = paymentStatusFilter;
      }

      const response = await axios.get(url, {
        params: params,
        headers: {
          Authorization: `${token}`,
        },
      });

      if (response?.data?.success && response && isMountedRef.current) {
        setOrderData(response?.data?.data?.orders || []);
        setTotalRows(response?.data?.data?.totalCount || 0);
        setIsLoading(false);
      } else {
        if (isMountedRef.current) {
          setOrderData([]);
          setTotalRows(0);
          setIsLoading(false);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setIsLoading(false);
        console.error('Error fetching order data:', error);

        if (error.response) {
          console.error('Error response:', error.response.data);
          console.error('Error status:', error.response.status);
        }

        setOrderData([]);
        setTotalRows(0);
      }
    }
  };

  const toggleModal = () => {
    setOrderId("");
    SetShowmodal(!showModal);
  };

  const toggleCancelOrderModal = () => {
    formik.resetForm();
    setOrderId("");
    setShowOrderCancellationPopup(!showOrderCancellationPopup);
  };

  const debouncedSearch = React.useRef(
    debounce(async (searchValue) => {
      setCurrentPage(1);
      setSearchTerm(searchValue);
    }, 500)
  ).current;

  const handleSearch = (event) => {
    const value = event.target.value;
    event.target.value = value;

    if (value.length >= 2 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleNavigate = (id) => {
    const currentIndex = orderData.findIndex(order => order._id === id);

    const navigationData = {
      currentIndex: (currentPage - 1) * perPage + currentIndex,
      totalOrders: totalRows,
      ordersList: orderData,
      filters: {
        search_string: searchTerm,
        start_date: startDate,
        end_date: endDate,
        status_filter: statusFilter,
        payment_status_filter: paymentStatusFilter,
        page: currentPage,
        limit: perPage
      }
    };

    navigate(`/orders/${id}`, {
      state: navigationData
    });
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${orderURL}/invoice-pdf/${orderId}`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `invoice-${orderId}.pdf`;
      link.click();
    } catch (error) {
      console.error("Error downloading the invoice:", error);
    }
  };

  const handlePaymentSuccess = async (row) => {
    const result = await Swal.fire({
      title: 'Have you checked payment success?',
      text: "This will mark the payment as successful",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, mark as success!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = await JSON.parse(localStorage.getItem("token"));
        await axios.post(
          `${orderURL}/payment-success/${row?.transaction_id}`,
          { status: "completed" },
          { headers: { authorization: token } }
        );

        Swal.fire({
          icon: "success",
          title: "Payment marked as successful!",
        });

        getOrderData();
      } catch (error) {
        console.error("Error marking payment as success:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to update payment status",
          text: error?.response?.data?.message || "Something went wrong",
        });
      }
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handlePaymentStatusFilterChange = (paymentStatus) => {
    setPaymentStatusFilter(paymentStatus);
    setCurrentPage(1);
  };

  const handleStatus = async (e) => {
    e.preventDefault();
    const token = await JSON.parse(localStorage.getItem("token"));
    let body = {
      order_status: statusValue
    };
    if (body.order_status === "") {
      return Swal.fire({
        icon: "error",
        title: "Provide Order Status",
      });
    }
    try {
      await axios
        .patch(`${orderURL}/update-order-status/${orderId}`, body, {
          headers: {
            Authorization: `${token}`,
          },
        })
        .then((res) => {
          if (res && res.status === 200) {
            Swal.fire({
              icon: "success",
              title: res?.data?.message,
            });
            SetShowmodal(false);
            setOrderId("");
            setStatusValue("");
            getOrderData();
          }
        });
    } catch (err) {
      console.error(err);
    }
  };

  function capitalizeFirstLetter(string) {
    return string?.charAt(0)?.toUpperCase() + string.slice(1);
  }

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/customer-detail/${customerId}`);
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    setCurrentPage(1);

    if (moment(newStartDate).isAfter(moment(endDate))) {
      setEndDate(newStartDate);
    }
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    if (!moment(newEndDate).isBefore(moment(startDate))) {
      setEndDate(newEndDate);
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    getOrderData();
  }, [currentPage, perPage, searchTerm, startDate, endDate, statusFilter, paymentStatusFilter]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Scheduled delivery slots are store-local (Australia) real-world
  // commitments, so this parsing stays tied to STORE_TZ regardless of viewer.
  const parseSlot = (slot) => {
    if (!slot) return null;

    let match = slot?.match(/(\d{2}):(\d{2})-(\d{2}):(\d{2})/);
    if (match) {
      const [, sH, sM, eH, eM] = match;
      return {
        start: { hour: +sH, minute: +sM },
        end: { hour: +eH, minute: +eM },
      };
    }

    match = slot?.match(/(\d{1,2})(AM|PM)-(\d{1,2})(AM|PM)/i);
    if (match) {
      const [, sH, sPeriod, eH, ePeriod] = match;

      const to24 = (h, period) => {
        h = +h;
        if (period.toUpperCase() === "PM" && h !== 12) h += 12;
        if (period.toUpperCase() === "AM" && h === 12) h = 0;
        return h;
      };

      return {
        start: { hour: to24(sH, sPeriod), minute: 0 },
        end: { hour: to24(eH, ePeriod), minute: 0 },
      };
    }

    return null;
  };

  const orderColumns = [
    {
      name: "STORE",
      width: "120px",
      selector: (row) => `${row.discard_from}`,
      cell: (row) => row?.store?.storeName,
      sortable: true,
      center: true,
    },
    {
      name: "#ORDER_ID",
      selector: (row) => row["sequence_number"],
      sortable: true,
      width: "150px",
      center: true,
      cell: (row) => (
        <div
          style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", cursor: "pointer", padding: "4px 0" }}
          onClick={() => handleNavigate(row?._id)}
        >
          <div style={{ display: "flex", alignItems: "center", color: '#007bff', gap: "6px" }}>
            {row?.sequence_number}
            {row?.comments && row.comments.trim() !== "" && (
              <FaRegStickyNote
                style={{ color: "orange", fontSize: "16px" }}
                title={`Instruction: ${row.comments}`}
              />
            )}
          </div>

          {row?.isScheduled && (
            <span
              title={`Scheduled: ${row.scheduledDate
                ? moment(row.scheduledDate).format("DD MMM YYYY")
                : ""
                }${row.scheduledTimeSlot ? " · " + row.scheduledTimeSlot : ""}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "10px",
                fontWeight: "600",
                color: "#7c3aed",
                background: "#ede9fe",
                border: "1px solid #c4b5fd",
                borderRadius: "999px",
                padding: "2px 7px",
                whiteSpace: "nowrap",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Scheduled
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Customer",
      width: "130px",
      selector: (row) => `${row.customer}`,
      sortable: true,
      cell: (row) => {
        const firstName =
          row?.users?.first_name || row?.address[0]?.first_name || "N/A";
        const lastName =
          row?.users?.last_name || row?.address[0]?.last_name || "N/A";
        return <div
          onClick={() => handleCustomerClick(row.users?._id)}
          style={{
            cursor: 'pointer',
            color: '#007bff',
            textDecoration: 'none',
            fontWeight: '500'
          }}
          className="customer-name-link"
          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
        >
          {`${firstName} ${lastName}`}
        </div>
      }
    },
    {
      name: "DATE",
      width: "130px",
      selector: (row) => `${row.createdAt}`,
      sortable: true,
      center: true,
      // Rendered in the VIEWER's local time (plain moment(), no .tz()) so
      // this matches the filter range built from viewerTZ above.
      cell: (row) => moment(row.createdAt).format("DD MMM YYYY hh:mm A"),
    },
    {
      name: "SCHED. TIME",
      width: "135px",
      selector: (row) => row?.scheduledDate || "",
      sortable: true,
      center: true,
      cell: (row) => {
        if (!row?.isScheduled || !row?.scheduledDate) {
          return <span style={{ color: "#bbb", fontSize: "12px" }}>—</span>;
        }
        return (
          <div style={{ textAlign: "center", lineHeight: "1.4" }}>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#7c3aed",
              }}
            >
              {/* Store-committed slot date — kept in store timezone */}
              {moment.tz(row.scheduledDate, STORE_TZ).format("DD MMM YYYY")}
            </div>
            {row?.scheduledTimeSlot && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#9333ea",
                  background: "#f3e8ff",
                  borderRadius: "4px",
                  padding: "1px 5px",
                  marginTop: "2px",
                  display: "inline-block",
                }}
              >
                {row.scheduledTimeSlot.replace(
                  /(\d{2}):(\d{2})-(\d{2}):(\d{2})/,
                  (_, h1, m1, h2, m2) => {
                    const fmt = (h) => `${+h > 12 ? +h - 12 : +h}${+h >= 12 ? "PM" : "AM"}`;
                    return `${fmt(h1)}–${fmt(h2)}`;
                  }
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "TOTAL ORDER",
      width: "120px",
      selector: (row) => `${row?.totalCustomerOrders}`,
      sortable: true,
      center: true,
    },
    {
      name: "PLATFORM",
      width: "115px",
      selector: (row) =>
        row?.orderSource
          ? row.orderSource.charAt(0).toUpperCase() + row.orderSource.slice(1).toLowerCase()
          : "",
      sortable: true,
      center: true,
    },
    {
      name: "TOTAL",
      selector: (row) => `${getOrderTotal(row)}`,
      width: "100px",
      cell: (row) => {
        const total = getOrderTotal(row);
        return total ? "$" + Number(total).toFixed(2) : "N/A";
      },
      sortable: true,
      center: true,
    },
    {
      name: "ITEM",
      width: "80px",
      selector: (row) => `${row?.totalItems}`,
      sortable: true,
      center: true,
    },
    {
      name: "DELIVERY ADDRESS",
      selector: (row) => `${row.address}`,
      width: "250px",
      cell: (row) => (
        <div style={{ textAlign: 'left', lineHeight: '1.3' }}>
          {row?.address[0]?.address ? (
            <div
              style={{
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                cursor: 'pointer',
                color: '#007bff',
                textDecoration: 'underline'
              }}
              onClick={() => {
                if (row.address[0].map_url) {
                  window.open(row.address[0].map_url, '_blank');
                }
              }}
              title="Click to view on map"
            >
              {row.address[0].address}
            </div>
          ) : (
            <div style={{ color: '#999' }}>No address available</div>
          )}
        </div>
      ),
      sortable: true,
      center: false,
    },
    {
      name: "PAYMENT",
      width: "110px",
      selector: (row) => `${row.transaction_status}`,
      cell: (row) => {
        const transactionColors = {
          complete: "success",
          completed: "success",
          pending: "warning",
          failed: "danger"
        };

        const statusKey = row?.transaction_status?.toLowerCase();
        const color = transactionColors[statusKey] || 'primary';

        return (
          <span style={{ fontSize: '13px' }} className={`badge badge-light-${color}`}>
            {capitalizeFirstLetter(row?.transaction_status)}
          </span>
        );
      },
      sortable: true,
      center: true,
    },
    {
      name: "STATUS",
      width: "110px",
      selector: (row) => `${row.order_status}`,
      cell: (row) => {
        const customColors = {
          accepted: "primary",
          pending: "warning",
          "on-the-way": "info",
          delivered: "success",
          rejected: "danger",
          cancelled: "danger",
          returned: "secondary",
        };

        const statusKey = row?.order_status?.toLowerCase();

        const color = customColors[statusKey] || 'primary';
        return (
          <span style={{ fontSize: '13px' }} className={`badge badge-light-${color}`}>
            {capitalizeFirstLetter(row?.order_status.replace(/-/g, ' '))}
          </span>
        );
      },
      sortable: true,
      center: true,
    },
    {
      name: "DELIVERY",
      width: "120px",
      selector: (row) => row.deliveryUser?.deliveryPartner?.full_name || "N/A",
      sortable: true,
      center: true,
      cell: (row) => (
        <div>
          <div>{row.deliveryUser?.deliveryPartner?.full_name || "N/A"}</div>
          <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
            {row.deliveryUser?.updatedAt ? moment(row.deliveryUser?.updatedAt).format("DD MMM YYYY hh:mm A") : " "}
          </div>
        </div>
      ),
    },
    {
      name: "D.TIME",
      width: "110px",
      selector: (row) => {
        if (row?.order_status === "cancelled") return -2;

        const paymentDone = ["completed", "complete"].includes(
          row?.transaction_status?.toLowerCase()
        );
        const isDelivered = !!row.deliveryUser?.updatedAt;

        if (!paymentDone && !isDelivered) return -1;

        const isScheduled =
          row?.isScheduled && row?.scheduledDate && row?.scheduledTimeSlot;

        const slot = parseSlot(row.scheduledTimeSlot);

        let startTime;

        if (isScheduled && slot) {
          // Store-committed slot — anchored to STORE_TZ, not viewer's zone.
          startTime = moment.tz(row.scheduledDate, STORE_TZ).set({
            hour: slot.start.hour,
            minute: slot.start.minute,
            second: 0,
            millisecond: 0,
          });

          if (now.isBefore(startTime) && !isDelivered) return -1;
        } else {
          startTime = moment(row.paymentSuccessTime ?? row.createdAt);
        }

        const endTime = isDelivered
          ? moment(row.deliveryUser.updatedAt)
          : now;

        return endTime.diff(startTime, "minutes");
      },

      sortable: true,
      center: true,

      cell: (row) => {
        if (row?.order_status === "cancelled") {
          return <div style={{ color: "#999" }}>Cancelled</div>;
        }

        const paymentDone = ["completed", "complete"].includes(
          row?.transaction_status?.toLowerCase()
        );
        const isDelivered = !!row.deliveryUser?.updatedAt;

        if (!paymentDone && !isDelivered) {
          return <div style={{ color: "#999" }}>N/A</div>;
        }

        const isScheduled =
          row?.isScheduled && row?.scheduledDate && row?.scheduledTimeSlot;

        let scheduledStart, scheduledEnd, slotDurationMin;

        const slot = parseSlot(row.scheduledTimeSlot);

        if (isScheduled && slot) {
          // Store-committed slot — anchored to STORE_TZ, not viewer's zone.
          scheduledStart = moment.tz(row.scheduledDate, STORE_TZ).set({
            hour: slot.start.hour,
            minute: slot.start.minute,
            second: 0,
            millisecond: 0,
          });

          scheduledEnd = moment.tz(row.scheduledDate, STORE_TZ).set({
            hour: slot.end.hour,
            minute: slot.end.minute,
            second: 0,
            millisecond: 0,
          });

          slotDurationMin = scheduledEnd.diff(scheduledStart, "minutes");
        }

        if (
          isScheduled &&
          scheduledStart &&
          now.isBefore(scheduledStart) &&
          !isDelivered
        ) {
          const minsToStart = scheduledStart.diff(now, "minutes");
          const h = Math.floor(minsToStart / 60);
          const m = minsToStart % 60;

          return (
            <div style={{ textAlign: "center", lineHeight: "1.3" }}>
              <div style={{ color: "#999" }}>N/A</div>
              <div style={{ fontSize: "9px", color: "#9333ea" }}>
                starts in {h > 0 ? `${h}h ${m}m` : `${m}m`}
              </div>
            </div>
          );
        }

        const startTime =
          isScheduled && scheduledStart
            ? scheduledStart
            : moment(row.paymentSuccessTime ?? row.createdAt);

        const endTime = isDelivered
          ? moment(row.deliveryUser.updatedAt)
          : now;

        const minutesDiff = endTime.diff(startTime, "minutes");

        if (minutesDiff < 0) {
          const abs = Math.abs(minutesDiff);
          const h = Math.floor(abs / 60);
          const m = abs % 60;

          return (
            <div style={{ color: "#28a745", fontWeight: "bold", textAlign: "center" }}>
              Early {h > 0 ? `${h}h ${m}m` : `${m}m`}
            </div>
          );
        }

        const hours = Math.floor(minutesDiff / 60);
        const minutes = minutesDiff % 60;
        const displayText =
          hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

        const getColor = (mins) => {
          if (isScheduled && slotDurationMin !== undefined) {
            if (mins <= 60) return "#28a745";
            if (mins <= slotDurationMin) return "#ffc107";
            return "#dc3545";
          }

          if (mins <= 33) return "#28a745";
          if (mins <= 50) return "#ffc107";
          return "#dc3545";
        };

        return (
          <div
            style={{
              color: getColor(minutesDiff),
              fontWeight: "bold",
              textAlign: "center",
              lineHeight: "1.3",
            }}
          >
            <div>{displayText}</div>
            {!isDelivered && (
              <div style={{ fontSize: "9px", opacity: 0.7 }}>
                live
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: 'Action',
      width: "90px",
      omit: !can('orders.change_status'),
      cell: (row) => (
        <div className='d-flex justify-content-end align-items-center' style={{ marginRight: '20px' }}>
          <div className='cursor-pointer'>
            <UncontrolledDropdown className='action_dropdown'>
              <UncontrolledDropdownToggle className='action_btn'>
                <MoreVertical color='#000' size={16} />
              </UncontrolledDropdownToggle>
              <UncontrolledDropdownMenu>
                <UncontrolledDropdownItem onClick={() => handleNavigate(row?._id)}>
                  View
                  <FaRegEye />
                </UncontrolledDropdownItem>
                {can('orders.change_status') && <UncontrolledDropdownItem onClick={() => {
                  toggleModal()
                  setOrderId(row?._id)
                  setStatusValue(row?.order_status)
                }}>
                  Change Status
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                </UncontrolledDropdownItem>}
                {can('orders.change_status') && (row.order_status !== 'cancelled') &&
                  <UncontrolledDropdownItem onClick={() => {
                    toggleCancelOrderModal()
                    setOrderId(row?._id)
                    setStatusValue("cancelled")
                  }}>
                    Cancel Order
                    <i className="fa fa-refresh" aria-hidden="true"></i>
                  </UncontrolledDropdownItem>}
                {can('orders.change_status') && (row?.transaction_status === 'pending') &&
                  <UncontrolledDropdownItem onClick={() => handlePaymentSuccess(row)}>
                    Payment Success
                    <i className="fa fa-check-circle" aria-hidden="true"></i>
                  </UncontrolledDropdownItem>
                }
                <UncontrolledDropdownItem onClick={() => downloadInvoice(row?._id)}>
                  Invoice
                  <FaDownload />
                </UncontrolledDropdownItem>
              </UncontrolledDropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>
      ),
      sortable: false,
      center: true,
    },
  ];

  return (
    <Fragment>
      {/* ============= HEADER ROW - ORDERS TITLE ============= */}
      <Row xxl={12} className="p-3">
        <Col md={12}>
          <div className="file-content align-items-center justify-content-between">
            <h5 className="fw-bolder">Orders</h5>
            {can('orders.export') &&
              // <Button
              //   className='btn btn-primary d-flex align-items-center'
              //   onClick={exportAllOrders}
              //   style={{ padding: '8px 18px', gap: '8px', whiteSpace: 'nowrap' }}
              //   title="Export all orders to CSV"
              // >
              //   <FaDownload size={16} />
              //   <span>Export</span>
              // </Button>
              <Button
                className='btn btn-primary d-flex align-items-center'
                onClick={exportAllOrders}
                disabled={isExporting}
                style={{ padding: '8px 18px', gap: '8px', whiteSpace: 'nowrap', opacity: isExporting ? 0.7 : 1 }}
                title="Export all orders to CSV"
              >
                {isExporting ? (
                  <>
                    <span
                      style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid rgba(255,255,255,0.5)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'spin 0.7s linear infinite',
                      }}
                    />
                    <span>Exporting…</span>
                  </>
                ) : (
                  <>
                    <FaDownload size={16} />
                    <span>Export</span>
                  </>
                )}
              </Button>
            }
          </div>
        </Col>
      </Row>

      {/* ============= FILTERS & SEARCH ROW ============= */}
      <Row xxl={12} className="mx-3 p-3 mb-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <Col md={9} lg={9}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>

            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>

              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <Label className="mb-0" style={{ whiteSpace: 'nowrap', fontWeight: '600', fontSize: '12px' }}>From:</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  max={moment().format('YYYY-MM-DD')}
                  style={{ width: '120px', padding: '6px 10px', fontSize: '12px' }}
                />
              </div>

              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <Label className="mb-0" style={{ whiteSpace: 'nowrap', fontWeight: '600', fontSize: '12px' }}>To:</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  min={startDate}
                  max={moment().format('YYYY-MM-DD')}
                  style={{ width: '120px', padding: '6px 10px', fontSize: '12px' }}
                />
              </div>

              <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle caret style={{ minWidth: '140px', padding: '8px 8px', fontSize: '11px' }}>
                  Status: {statusFilter === 'all' ? 'All' : capitalizeFirstLetter(statusFilter.replace(/-/g, ' '))}
                </DropdownToggle>
                <DropdownMenu style={{ fontSize: '12px' }}>
                  <DropdownItem onClick={() => handleStatusFilterChange('all')}>All</DropdownItem>
                  <DropdownItem onClick={() => handleStatusFilterChange('accepted')}>Accepted</DropdownItem>
                  <DropdownItem onClick={() => handleStatusFilterChange('pending')}>Pending</DropdownItem>
                  <DropdownItem onClick={() => handleStatusFilterChange('on-the-way')}>On The Way</DropdownItem>
                  <DropdownItem onClick={() => handleStatusFilterChange('delivered')}>Delivered</DropdownItem>
                  <DropdownItem onClick={() => handleStatusFilterChange('cancelled')}>Cancelled</DropdownItem>
                </DropdownMenu>
              </Dropdown>

              <Dropdown isOpen={paymentDropdownOpen} toggle={togglePaymentDropdown}>
                <DropdownToggle caret style={{ minWidth: '160px', padding: '8px 8px', fontSize: '11px' }}>
                  Payment: {paymentStatusFilter === 'all' ? 'All' : capitalizeFirstLetter(paymentStatusFilter)}
                </DropdownToggle>
                <DropdownMenu style={{ fontSize: '12px' }}>
                  <DropdownItem onClick={() => handlePaymentStatusFilterChange('all')}>All</DropdownItem>
                  <DropdownItem onClick={() => handlePaymentStatusFilterChange('pending')}>Pending</DropdownItem>
                  <DropdownItem onClick={() => handlePaymentStatusFilterChange('completed')}>Completed</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>


          </div>
        </Col>

        <Col md={3} lg={3}>
          <div className="form-group position-relative search_outer d-flex align-items-center">
            <i className="fa fa-search"></i>
            <input
              className="form-control border-0"
              defaultValue={searchTerm}
              onChange={(e) => handleSearch(e)}
              type="text"
              placeholder="Search..."
              style={{ paddingLeft: '35px' }}
            />
          </div>
        </Col>
      </Row>

      {/* ============= DATA TABLE ============= */}
      <Row xxl={12} className="p-3">
        <Col md={12}>
          <DataTable
            data={orderData}
            columns={orderColumns}
            conditionalRowStyles={[
              {
                when: (row) => row?.isScheduled === true,
                style: {
                  backgroundColor: "#faf5ff",
                  borderLeft: "3px solid #7c3aed",
                },
              },
            ]}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            onSelectedRowsChange={handleRowSelected}
            clearSelectedRows={toggleDelet}
            progressPending={isLoading}
            progressComponent={<Loader />}
          />
        </Col>
      </Row>

      <CommonModal isOpen={showOrderCancellationPopup} title={"Cancel Order"} className="store_modal" toggler={toggleCancelOrderModal} size="md">
        <Container>
          <form className="p-2 md:p-4" onSubmit={formik.handleSubmit}>
            <div className="flex flex-col gap-4">
              <label
                style={{
                  fontFamily: 'Lato',
                  fontSize: '18px',
                  fontWeight: '400',
                  textAlign: 'left',
                }}
              >
                Please enter the reason for cancellation
              </label>
              <input
                type="text"
                id="reason"
                name='cancel_reason'
                placeholder="Enter reason for cancellation"
                className={`w-[100%] focus:outline-none border-b-2 p-2`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.cancel_reason}
              />
              {formik.touched.cancel_reason && formik.errors.cancel_reason ? (
                <span className="text-red-500 text-sm mt-2">{formik.errors.cancel_reason}</span>
              ) : (
                ""
              )}
            </div>
            <div className="flex justify-center gap-4">
              <button
                className="bg-[#D3178A] mt-4 w-32 h-12 rounded-full text-white"
                type="submit"
              >
                Cancel Order
              </button>
            </div>
          </form>
        </Container>
      </CommonModal>

      <CommonModal
        isOpen={showModal}
        title={"Change Status"}
        className="store_modal"
        toggler={toggleModal}
        size="md"
      >
        <Container>
          <Form className="status_form" onSubmit={handleStatus}>
            <>
              <Col lg={12}>
                <FormGroup>
                  <Row>
                    <Col>
                      <Label>
                        <Input
                          type="radio"
                          name="status"
                          className="me-2"
                          id="1"
                          value={"accepted"}
                          checked={statusValue === "accepted"}
                          onChange={(e) => setStatusValue(e.target.value)}
                        />
                        Accepted
                      </Label>
                    </Col>
                    <Col>
                      <Label>
                        <Input
                          type="radio"
                          name="status"
                          className="me-2"
                          value="delivered"
                          id="1"
                          checked={statusValue === "delivered"}
                          onChange={(e) => setStatusValue(e.target.value)}
                        />
                        Delivered
                      </Label>
                    </Col>
                  </Row>
                </FormGroup>
              </Col>
            </>
            <Row>
              <Col xxl={12} className="text-right mt-3">
                <Button
                  type="button"
                  className="cancel_Btn"
                  onClick={() => SetShowmodal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="cursor-pointer bg-[#ff0000] font-medium w-40 ms-2 px-2 py-2 rounded-2xl text-white flex justify-center items-center"
                >
                  Change Status
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </CommonModal>
    </Fragment>
  );
};

export default DataTableComponent;

// old code before updating time and b2b tax update
// import React, {
//   Fragment,
//   useCallback,
//   useEffect,
//   useRef,
//   useState,
// } from "react";
// import DataTable from "react-data-table-component";
// import { H5 } from "../../../../AbstractElements";
// import {
//   Button,
//   Col,
//   Container,
//   Form,
//   FormGroup,
//   Input,
//   Label,
//   Row,
//   Dropdown,
//   DropdownToggle,
//   DropdownMenu,
//   DropdownItem,
// } from "reactstrap";
// import CommonModal from "../../../UiKits/Modals/common/modal";
// import {
//   DropdownItem as UncontrolledDropdownItem,
//   DropdownMenu as UncontrolledDropdownMenu,
//   DropdownToggle as UncontrolledDropdownToggle,
//   UncontrolledDropdown,
// } from "reactstrap";
// import { MoreVertical, } from "react-feather";
// import { FaDownload, FaRegEye, FaRegStickyNote, FaFileExport } from "react-icons/fa";
// import { useNavigate } from "react-router";
// import axios from "axios";
// import { baseURL, orderURL } from "../../../../Services/api/baseURL";
// import Loader from "../../../Loader/Loader";
// import { debounce } from "lodash";
// import Swal from "sweetalert2";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import moment from "moment-timezone";
// import Papa from "papaparse";
// import { saveAs } from "file-saver";
// import useRbac from "../../../../Hooks/useRbac";

// const DataTableComponent = () => {
//   const navigate = useNavigate();
//   const { can } = useRbac();
//   const userRole = JSON.parse(localStorage.getItem("role_name"));
//   const [selectedRows, setSelectedRows] = useState([]);
//   const [orderData, setOrderData] = useState([]);
//   const [toggleDelet, setToggleDelet] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [showModal, SetShowmodal] = useState(false);
//   const [totalRows, setTotalRows] = useState(0);
//   const [perPage, setPerPage] = useState(10);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [orderId, setOrderId] = useState('');
//   const [statusValue, setStatusValue] = useState('');
//   const [showOrderCancellationPopup, setShowOrderCancellationPopup] = useState(false);
//   const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
//   const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
//   const isMountedRef = useRef(true);
//   const [statusFilter, setStatusFilter] = useState('all');
//   const [dropdownOpen, setDropdownOpen] = useState(false);
//   const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
//   const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
//   const toggleDropdown = () => setDropdownOpen(prevState => !prevState);
//   const togglePaymentDropdown = () => setPaymentDropdownOpen(prevState => !prevState);
//   const [now, setNow] = useState(moment());

//   useEffect(() => {
//     const interval = setInterval(() => setNow(moment()), 60000); // tick every minute
//     return () => clearInterval(interval);
//   }, []);

//   const formik = useFormik({
//     initialValues: {
//       cancel_reason: "",
//     },
//     validationSchema: Yup.object({
//       cancel_reason: Yup.string().required(
//         "Please Enter Reason for Order Cancellation"
//       ),
//     }),
//     onSubmit: async (values) => {
//       const token = await JSON.parse(localStorage.getItem("token"));
//       let body = {
//         order_status: statusValue,
//         cancel_reason: formik.values.cancel_reason,
//       };
//       if (body.order_status === "") {
//         return Swal.fire({
//           icon: "error",
//           title: "Provide Order Status",
//         });
//       }
//       try {
//         await axios
//           .patch(`${orderURL}/update-order-status/${orderId}`, body, {
//             headers: {
//               Authorization: `${token}`,
//             },
//           })
//           .then((res) => {
//             if (res && res.status === 200) {
//               Swal.fire({
//                 icon: "success",
//                 title: res?.data?.message,
//               });
//               setShowOrderCancellationPopup(false);
//               setOrderId("");
//               setStatusValue("");
//               formik.setFieldValue("cancel_reason", "");
//               getOrderData();
//             }
//           });
//       } catch (err) {
//         console.error(err);
//       }
//     },
//   });

//   const exportAllOrders = async () => {
//     try {
//       const token = JSON.parse(localStorage.getItem("token"));
//       const userData = JSON.parse(localStorage.getItem("UserData"));

//       let params = {
//         search_string: searchTerm || "",
//         start_date: startDate
//           ? moment.tz(startDate, "Australia/Sydney").startOf("day").utc().format()
//           : undefined,
//         end_date: endDate
//           ? moment.tz(endDate, "Australia/Sydney").endOf("day").utc().format()
//           : undefined,
//         order_status: statusFilter !== "all" ? statusFilter : undefined,
//         payment_status: paymentStatusFilter !== "all" ? paymentStatusFilter : undefined,
//         export: true,
//         page: 1,        // 👈 Fix 1: always start from page 1
//         limit: 999999,  // 👈 Fix 1: large limit to bypass pagination
//       };

//       if (userRole === "store" && userData?._id) {
//         params.storeId = userData._id;
//       }

//       const response = await axios.get(
//         `${baseURL}/api/dashboard/get-all-orders-dashboard-export`,
//         {
//           params,
//           headers: { Authorization: token },
//         }
//       );

//       const orders = response?.data?.data?.orders || [];

//       if (!orders.length) {
//         Swal.fire("No data to export");
//         return;
//       }

//       // 👈 Fix 2: format date as plain text to avoid ###### in Excel
//       const csvData = orders.map((order) => ({
//         Order_ID: order.sequence_number,
//         Customer: `${order?.users?.first_name || ""} ${order?.users?.last_name || ""}`.trim(),
//         Phone: order?.users?.phone || order?.address?.[0]?.addressPhoneNumber || "",
//         Date: `="${moment(order.createdAt).format("DD-MM-YYYY HH:mm")}"`, // 👈 Excel text prefix trick
//         Total_Amount: order.order_value,
//         Payment_Status: order.transaction_status,
//         Order_Status: order.order_status,
//         Store: order?.store?.storeName || "",
//         Platform: order.orderSource,
//       }));

//       const csv = Papa.unparse(csvData);
//       const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }); // 👈 BOM for Excel encoding
//       saveAs(blob, `orders_${moment().format("YYYYMMDD_HHmm")}.csv`);

//     } catch (error) {
//       console.error(error);
//       Swal.fire("Failed to export orders");
//     }
//   };

//   const getOrderData = async () => {
//     setIsLoading(true);

//     try {
//       const token = await JSON.parse(localStorage.getItem("token"));
//       const userData = await JSON.parse(localStorage.getItem("UserData"));
//       let url =
//         userRole === "store"
//           ? `${baseURL}/api/dashboard/get-all-orders-dashboard`
//           : `${baseURL}/api/dashboard/get-all-orders-dashboard`;

//       let params = {
//         search_string: searchTerm || '',
//         page: currentPage,
//         limit: perPage
//       };

//       if (userRole === "store" && userData?._id) {
//         params.storeId = userData._id;
//       }

//       // if (startDate && endDate) {
//       //   params.start_date = moment(startDate).format("YYYY-MM-DD");
//       //   params.end_date = moment(endDate).format("YYYY-MM-DD");
//       // }
//       if (startDate && endDate) {
//         params.start_date = moment.tz(startDate, "Australia/Sydney").startOf('day').utc().format("YYYY-MM-DD HH:mm:ss");
//         params.end_date = moment.tz(endDate, "Australia/Sydney").endOf('day').utc().format("YYYY-MM-DD HH:mm:ss");
//       }

//       if (statusFilter !== 'all') {
//         params.order_status = statusFilter;
//       }
//       if (paymentStatusFilter !== 'all') {
//         params.payment_status = paymentStatusFilter;
//       }

//       const response = await axios.get(url, {
//         params: params,
//         headers: {
//           Authorization: `${token}`,
//         },
//       });

//       console.log('Order data response:', response);

//       if (response?.data?.success && response && isMountedRef.current) {
//         setOrderData(response?.data?.data?.orders || []);
//         setTotalRows(response?.data?.data?.totalCount || 0);
//         setIsLoading(false);
//       } else {
//         if (isMountedRef.current) {
//           setOrderData([]);
//           setTotalRows(0);
//           setIsLoading(false);
//         }
//       }
//     } catch (error) {
//       if (isMountedRef.current) {
//         setIsLoading(false);
//         console.error('Error fetching order data:', error);

//         if (error.response) {
//           console.error('Error response:', error.response.data);
//           console.error('Error status:', error.response.status);
//         }

//         setOrderData([]);
//         setTotalRows(0);
//       }
//     }
//   };

//   const toggleModal = () => {
//     setOrderId("");
//     SetShowmodal(!showModal);
//   };

//   const toggleCancelOrderModal = () => {
//     formik.resetForm();
//     setOrderId("");
//     setShowOrderCancellationPopup(!showOrderCancellationPopup);
//   };

//   const debouncedSearch = React.useRef(
//     debounce(async (searchValue) => {
//       // Reset to page 1 when searching
//       setCurrentPage(1);
//       setSearchTerm(searchValue);
//     }, 500)
//   ).current;

//   const handleSearch = (event) => {
//     const value = event.target.value;
//     event.target.value = value;

//     if (value.length >= 2 || value.length === 0) {
//       debouncedSearch(value);
//     }
//   };

//   const handleRowSelected = useCallback((state) => {
//     setSelectedRows(state.selectedRows);
//   }, []);

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const handlePerRowsChange = async (newPerPage, page) => {
//     setPerPage(newPerPage);
//     setCurrentPage(page);
//   };

//   const handleNavigate = (id) => {
//     const currentIndex = orderData.findIndex(order => order._id === id);

//     const navigationData = {
//       currentIndex: (currentPage - 1) * perPage + currentIndex,
//       totalOrders: totalRows,
//       ordersList: orderData,
//       filters: {
//         search_string: searchTerm,
//         start_date: startDate,
//         end_date: endDate,
//         status_filter: statusFilter,
//         payment_status_filter: paymentStatusFilter,
//         page: currentPage,
//         limit: perPage
//       }
//     };

//     navigate(`/orders/${id}`, {
//       state: navigationData
//     });
//   };

//   console.log('orderData:', orderData);

//   const downloadInvoice = async (orderId) => {
//     try {
//       const response = await axios.get(`${orderURL}/invoice-pdf/${orderId}`, {
//         responseType: "blob",
//       });

//       const blob = new Blob([response.data], { type: "application/pdf" });
//       const link = document.createElement("a");
//       link.href = window.URL.createObjectURL(blob);
//       link.download = `invoice-${orderId}.pdf`;
//       link.click();
//     } catch (error) {
//       console.error("Error downloading the invoice:", error);
//     }
//   };

//   const handlePaymentSuccess = async (row) => {
//     const result = await Swal.fire({
//       title: 'Have you checked payment success?',
//       text: "This will mark the payment as successful",
//       icon: 'warning',
//       showCancelButton: true,
//       confirmButtonColor: '#3085d6',
//       cancelButtonColor: '#d33',
//       confirmButtonText: 'Yes, mark as success!',
//       cancelButtonText: 'Cancel'
//     });

//     if (result.isConfirmed) {
//       try {
//         const token = await JSON.parse(localStorage.getItem("token"));
//         await axios.post(
//           `${orderURL}/payment-success/${row?.transaction_id}`,
//           { status: "completed" },
//           { headers: { authorization: token } }
//         );


//         Swal.fire({
//           icon: "success",
//           title: "Payment marked as successful!",
//         });

//         getOrderData();
//       } catch (error) {
//         console.error("Error marking payment as success:", error);
//         Swal.fire({
//           icon: "error",
//           title: "Failed to update payment status",
//           text: error?.response?.data?.message || "Something went wrong",
//         });
//       }
//     }
//   };

//   const handleStatusFilterChange = (status) => {
//     setStatusFilter(status);
//     setCurrentPage(1);
//   };

//   const handlePaymentStatusFilterChange = (paymentStatus) => {
//     setPaymentStatusFilter(paymentStatus);
//     setCurrentPage(1);
//   };

//   const handleStatus = async (e) => {
//     e.preventDefault();
//     const token = await JSON.parse(localStorage.getItem("token"));
//     let body = {
//       order_status: statusValue
//     };
//     if (body.order_status === "") {
//       return Swal.fire({
//         icon: "error",
//         title: "Provide Order Status",
//       });
//     }
//     try {
//       await axios
//         .patch(`${orderURL}/update-order-status/${orderId}`, body, {
//           headers: {
//             Authorization: `${token}`,
//           },
//         })
//         .then((res) => {
//           if (res && res.status === 200) {
//             Swal.fire({
//               icon: "success",
//               title: res?.data?.message,
//             });
//             SetShowmodal(false);
//             setOrderId("");
//             setStatusValue("");
//             getOrderData();
//           }
//         });
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   function capitalizeFirstLetter(string) {
//     return string?.charAt(0)?.toUpperCase() + string.slice(1);
//   }

//   const handleCustomerClick = (customerId) => {
//     navigate(`/customers/customer-detail/${customerId}`);
//   };

//   const handleStartDateChange = (e) => {
//     const newStartDate = e.target.value;
//     setStartDate(newStartDate);
//     setCurrentPage(1);

//     if (moment(newStartDate).isAfter(moment(endDate))) {
//       setEndDate(newStartDate);
//     }
//   };

//   const handleEndDateChange = (e) => {
//     const newEndDate = e.target.value;
//     if (!moment(newEndDate).isBefore(moment(startDate))) {
//       setEndDate(newEndDate);
//       setCurrentPage(1);
//     }
//   };

//   useEffect(() => {
//     getOrderData();
//   }, [currentPage, perPage, searchTerm, startDate, endDate, statusFilter, paymentStatusFilter]);

//   useEffect(() => {
//     return () => {
//       isMountedRef.current = false;
//     };
//   }, []);

//   const parseSlot = (slot) => {
//     if (!slot) return null;

//     // 24h format: 18:00-20:00
//     let match = slot?.match(/(\d{2}):(\d{2})-(\d{2}):(\d{2})/);
//     if (match) {
//       const [, sH, sM, eH, eM] = match;
//       return {
//         start: { hour: +sH, minute: +sM },
//         end: { hour: +eH, minute: +eM },
//       };
//     }

//     // 12h format: 6PM-8PM
//     match = slot?.match(/(\d{1,2})(AM|PM)-(\d{1,2})(AM|PM)/i);
//     if (match) {
//       const [, sH, sPeriod, eH, ePeriod] = match;

//       const to24 = (h, period) => {
//         h = +h;
//         if (period.toUpperCase() === "PM" && h !== 12) h += 12;
//         if (period.toUpperCase() === "AM" && h === 12) h = 0;
//         return h;
//       };

//       return {
//         start: { hour: to24(sH, sPeriod), minute: 0 },
//         end: { hour: to24(eH, ePeriod), minute: 0 },
//       };
//     }

//     return null;
//   };

//   const orderColumns = [
//     // {
//     //   name: "#ORDER_ID",
//     //   selector: (row) => row["sequence_number"],
//     //   sortable: true,
//     //   width: "150px",
//     //   center: true,
//     //   cell: (row) => (
//     //     <div
//     //       className="ellipses_text_3"
//     //       style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
//     //       onClick={() => handleNavigate(row?._id)}
//     //     >
//     //       {row?.sequence_number}
//     //       {row?.comments && row.comments.trim() !== "" && (
//     //         <FaRegStickyNote
//     //           style={{ color: "orange", fontSize: "20px" }}
//     //           title={`Instruction: ${row.comments}`}
//     //         />
//     //       )}
//     //     </div>
//     //   ),
//     // },
//     {
//       name: "STORE",
//       width: "120px",
//       selector: (row) => `${row.discard_from}`,
//       cell: (row) => row?.store?.storeName,
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "#ORDER_ID",
//       selector: (row) => row["sequence_number"],
//       sortable: true,
//       width: "150px",   // slightly wider to fit badge
//       center: true,
//       cell: (row) => (
//         <div
//           style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", cursor: "pointer", padding: "4px 0" }}
//           onClick={() => handleNavigate(row?._id)}
//         >
//           {/* Order ID row with note icon */}
//           <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
//             {row?.sequence_number}
//             {row?.comments && row.comments.trim() !== "" && (
//               <FaRegStickyNote
//                 style={{ color: "orange", fontSize: "16px" }}
//                 title={`Instruction: ${row.comments}`}
//               />
//             )}
//           </div>

//           {/* Scheduled badge */}
//           {row?.isScheduled && (
//             <span
//               title={`Scheduled: ${row.scheduledDate
//                 ? moment(row.scheduledDate).format("DD MMM YYYY")
//                 : ""
//                 }${row.scheduledTimeSlot ? " · " + row.scheduledTimeSlot : ""}`}
//               style={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 gap: "4px",
//                 fontSize: "10px",
//                 fontWeight: "600",
//                 color: "#7c3aed",
//                 background: "#ede9fe",
//                 border: "1px solid #c4b5fd",
//                 borderRadius: "999px",
//                 padding: "2px 7px",
//                 whiteSpace: "nowrap",
//               }}
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 width="10"
//                 height="10"
//                 viewBox="0 0 24 24"
//                 fill="none"
//                 stroke="currentColor"
//                 strokeWidth="2.5"
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//               >
//                 <circle cx="12" cy="12" r="10" />
//                 <polyline points="12 6 12 12 16 14" />
//               </svg>
//               Scheduled
//             </span>
//           )}
//         </div>
//       ),
//     },
//     {
//       name: "Customer",
//       width: "130px",
//       selector: (row) => `${row.customer}`,
//       sortable: true,
//       cell: (row) => {
//         const firstName =
//           row?.users?.first_name || row?.address[0]?.first_name || "N/A";
//         const lastName =
//           row?.users?.last_name || row?.address[0]?.last_name || "N/A";
//         return <div
//           onClick={() => handleCustomerClick(row.users?._id)}
//           style={{
//             cursor: 'pointer',
//             color: '#007bff',
//             textDecoration: 'none',
//             fontWeight: '500'
//           }}
//           className="customer-name-link"
//           onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
//           onMouseOut={(e) => e.target.style.textDecoration = 'none'}
//         >
//           {`${firstName} ${lastName}`}
//         </div>
//       }
//     },
//     {
//       name: "DATE",
//       width: "130px",
//       selector: (row) => `${row.createdAt}`,
//       sortable: true,
//       center: true,
//       cell: (row) => moment(row.createdAt).format("DD MMM YYYY hh:mm A"),
//     },
//     {
//       name: "SCHED. TIME",
//       width: "135px",
//       selector: (row) => row?.scheduledDate || "",
//       sortable: true,
//       center: true,
//       cell: (row) => {
//         if (!row?.isScheduled || !row?.scheduledDate) {
//           return <span style={{ color: "#bbb", fontSize: "12px" }}>—</span>;
//         }
//         return (
//           <div style={{ textAlign: "center", lineHeight: "1.4" }}>
//             <div
//               style={{
//                 fontSize: "12px",
//                 fontWeight: "600",
//                 color: "#7c3aed",
//               }}
//             >
//               {moment(row.scheduledDate).format("DD MMM YYYY")}
//             </div>
//             {row?.scheduledTimeSlot && (
//               <div
//                 style={{
//                   fontSize: "11px",
//                   color: "#9333ea",
//                   background: "#f3e8ff",
//                   borderRadius: "4px",
//                   padding: "1px 5px",
//                   marginTop: "2px",
//                   display: "inline-block",
//                 }}
//               >
//                 {row.scheduledTimeSlot.replace(
//                   /(\d{2}):(\d{2})-(\d{2}):(\d{2})/,
//                   (_, h1, m1, h2, m2) => {
//                     const fmt = (h) => `${+h > 12 ? +h - 12 : +h}${+h >= 12 ? "PM" : "AM"}`;
//                     return `${fmt(h1)}–${fmt(h2)}`;
//                   }
//                 )}
//               </div>
//             )}
//           </div>
//         );
//       },
//     },
//     {
//       name: "TOTAL ORDER",
//       width: "120px",
//       selector: (row) => `${row?.totalCustomerOrders}`,
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "PLATFORM",
//       width: "115px",
//       selector: (row) =>
//         row?.orderSource
//           ? row.orderSource.charAt(0).toUpperCase() + row.orderSource.slice(1).toLowerCase()
//           : "",
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "TOTAL",
//       selector: (row) => `${row?.order_value}`,
//       width: "100px",
//       cell: (row) =>
//         row?.order_value ? "$" + row?.order_value.toFixed(2) : "N/A",
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "ITEM",
//       width: "80px",
//       selector: (row) => `${row?.totalItems}`,
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "DELIVERY ADDRESS",
//       selector: (row) => `${row.address}`,
//       width: "250px",
//       cell: (row) => (
//         <div style={{ textAlign: 'left', lineHeight: '1.3' }}>
//           {row?.address[0]?.address ? (
//             <div
//               style={{
//                 wordWrap: 'break-word',
//                 whiteSpace: 'normal',
//                 cursor: 'pointer',
//                 color: '#007bff',
//                 textDecoration: 'underline'
//               }}
//               onClick={() => {
//                 if (row.address[0].map_url) {
//                   window.open(row.address[0].map_url, '_blank');
//                 }
//               }}
//               title="Click to view on map"
//             >
//               {row.address[0].address}
//             </div>
//           ) : (
//             <div style={{ color: '#999' }}>No address available</div>
//           )}
//         </div>
//       ),
//       sortable: true,
//       center: false,
//     },
//     {
//       name: "PAYMENT",
//       width: "110px",
//       selector: (row) => `${row.transaction_status}`,
//       cell: (row) => {
//         const transactionColors = {
//           complete: "success",
//           completed: "success",
//           pending: "warning",
//           failed: "danger"
//         };

//         const statusKey = row?.transaction_status?.toLowerCase();
//         const color = transactionColors[statusKey] || 'primary';

//         return (
//           <span style={{ fontSize: '13px' }} className={`badge badge-light-${color}`}>
//             {capitalizeFirstLetter(row?.transaction_status)}
//           </span>
//         );
//       },
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "STATUS",
//       width: "110px",
//       selector: (row) => `${row.order_status}`,
//       cell: (row) => {
//         const customColors = {
//           accepted: "primary",
//           pending: "warning",
//           "on-the-way": "info",
//           delivered: "success",
//           rejected: "danger",
//           cancelled: "danger",
//           returned: "secondary",
//         };

//         const statusKey = row?.order_status?.toLowerCase();

//         const color = customColors[statusKey] || 'primary';
//         return (
//           <span style={{ fontSize: '13px' }} className={`badge badge-light-${color}`}>
//             {capitalizeFirstLetter(row?.order_status.replace(/-/g, ' '))}
//           </span>
//         );
//       },
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "DELIVERY",
//       width: "120px",
//       selector: (row) => row.deliveryUser?.deliveryPartner?.full_name || "N/A",
//       sortable: true,
//       center: true,
//       cell: (row) => (
//         <div>
//           <div>{row.deliveryUser?.deliveryPartner?.full_name || "N/A"}</div>
//           <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
//             {row.deliveryUser?.updatedAt ? moment(row.deliveryUser?.updatedAt).format("DD MMM YYYY hh:mm A") : " "}
//           </div>
//         </div>
//       ),
//     },
//     // {
//     //   name: "D.TIME",
//     //   width: "100px",
//     //   selector: (row) => {
//     //     if (row?.order_status == "cancelled") return "Cancelled";
//     //     if (!row.deliveryUser?.updatedAt) return "N/A";
//     //     const orderTime = moment(row.paymentSuccessTime ?? row.createdAt);
//     //     const deliveryTime = moment(row.deliveryUser?.updatedAt);
//     //     return deliveryTime.diff(orderTime, 'minutes');
//     //   },
//     //   sortable: true,
//     //   center: true,
//     //   cell: (row) => {
//     //     if (!row.deliveryUser?.updatedAt) {
//     //       return <div style={{ color: '#999' }}>N/A</div>;
//     //     }

//     //     const orderTime = moment(row.paymentSuccessTime ?? row.createdAt);
//     //     const deliveryTime = moment(row.deliveryUser?.updatedAt);
//     //     const minutesDiff = deliveryTime.diff(orderTime, 'minutes');

//     //     const hours = Math.floor(minutesDiff / 60);
//     //     const minutes = minutesDiff % 60;

//     //     let displayText;
//     //     if (hours > 0) {
//     //       displayText = `${hours}h ${minutes}m`;
//     //     } else {
//     //       displayText = `${minutes}m`;
//     //     }
//     //     const getColor = (mins) => {
//     //       if (mins <= 33) return '#28a745';
//     //       if (mins <= 50) return '#ffc107';
//     //       return '#dc3545';
//     //     };

//     //     return (
//     //       <div style={{
//     //         color: getColor(minutesDiff),
//     //         fontWeight: 'bold'
//     //       }}>
//     //         {displayText}
//     //       </div>
//     //     );
//     //   },
//     // },
//     {
//       name: "D.TIME",
//       width: "110px",
//       selector: (row) => {
//         if (row?.order_status === "cancelled") return -2;

//         const paymentDone = ["completed", "complete"].includes(
//           row?.transaction_status?.toLowerCase()
//         );
//         const isDelivered = !!row.deliveryUser?.updatedAt;

//         if (!paymentDone && !isDelivered) return -1;

//         const isScheduled =
//           row?.isScheduled && row?.scheduledDate && row?.scheduledTimeSlot;

//         const slot = parseSlot(row.scheduledTimeSlot);

//         let startTime;

//         if (isScheduled && slot) {
//           startTime = moment.tz(row.scheduledDate, "Australia/Sydney").set({
//             hour: slot.start.hour,
//             minute: slot.start.minute,
//             second: 0,
//             millisecond: 0,
//           });

//           if (now.isBefore(startTime) && !isDelivered) return -1;
//         } else {
//           startTime = moment(row.paymentSuccessTime ?? row.createdAt);
//         }

//         const endTime = isDelivered
//           ? moment(row.deliveryUser.updatedAt)
//           : now;

//         return endTime.diff(startTime, "minutes");
//       },

//       sortable: true,
//       center: true,

//       cell: (row) => {
//         if (row?.order_status === "cancelled") {
//           return <div style={{ color: "#999" }}>Cancelled</div>;
//         }

//         const paymentDone = ["completed", "complete"].includes(
//           row?.transaction_status?.toLowerCase()
//         );
//         const isDelivered = !!row.deliveryUser?.updatedAt;

//         if (!paymentDone && !isDelivered) {
//           return <div style={{ color: "#999" }}>N/A</div>;
//         }

//         const isScheduled =
//           row?.isScheduled && row?.scheduledDate && row?.scheduledTimeSlot;

//         let scheduledStart, scheduledEnd, slotDurationMin;

//         const slot = parseSlot(row.scheduledTimeSlot);

//         if (isScheduled && slot) {
//           scheduledStart = moment.tz(row.scheduledDate, "Australia/Sydney").set({
//             hour: slot.start.hour,
//             minute: slot.start.minute,
//             second: 0,
//             millisecond: 0,
//           });

//           scheduledEnd = moment.tz(row.scheduledDate, "Australia/Sydney").set({
//             hour: slot.end.hour,
//             minute: slot.end.minute,
//             second: 0,
//             millisecond: 0,
//           });

//           slotDurationMin = scheduledEnd.diff(scheduledStart, "minutes");
//         }

//         // Before slot start → show countdown
//         if (
//           isScheduled &&
//           scheduledStart &&
//           now.isBefore(scheduledStart) &&
//           !isDelivered
//         ) {
//           const minsToStart = scheduledStart.diff(now, "minutes");
//           const h = Math.floor(minsToStart / 60);
//           const m = minsToStart % 60;

//           return (
//             <div style={{ textAlign: "center", lineHeight: "1.3" }}>
//               <div style={{ color: "#999" }}>N/A</div>
//               <div style={{ fontSize: "9px", color: "#9333ea" }}>
//                 starts in {h > 0 ? `${h}h ${m}m` : `${m}m`}
//               </div>
//             </div>
//           );
//         }

//         const startTime =
//           isScheduled && scheduledStart
//             ? scheduledStart
//             : moment(row.paymentSuccessTime ?? row.createdAt);

//         const endTime = isDelivered
//           ? moment(row.deliveryUser.updatedAt)
//           : now;

//         const minutesDiff = endTime.diff(startTime, "minutes");

//         // Early delivery
//         if (minutesDiff < 0) {
//           const abs = Math.abs(minutesDiff);
//           const h = Math.floor(abs / 60);
//           const m = abs % 60;

//           return (
//             <div style={{ color: "#28a745", fontWeight: "bold", textAlign: "center" }}>
//               Early {h > 0 ? `${h}h ${m}m` : `${m}m`}
//             </div>
//           );
//         }

//         const hours = Math.floor(minutesDiff / 60);
//         const minutes = minutesDiff % 60;
//         const displayText =
//           hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

//         const getColor = (mins) => {
//           if (isScheduled && slotDurationMin !== undefined) {
//             if (mins <= 60) return "#28a745"; // green
//             if (mins <= slotDurationMin) return "#ffc107"; // yellow
//             return "#dc3545"; // red
//           }

//           // fallback (non-scheduled)
//           if (mins <= 33) return "#28a745";
//           if (mins <= 50) return "#ffc107";
//           return "#dc3545";
//         };

//         return (
//           <div
//             style={{
//               color: getColor(minutesDiff),
//               fontWeight: "bold",
//               textAlign: "center",
//               lineHeight: "1.3",
//             }}
//           >
//             <div>{displayText}</div>
//             {!isDelivered && (
//               <div style={{ fontSize: "9px", opacity: 0.7 }}>
//                 live
//               </div>
//             )}
//           </div>
//         );
//       },
//     },
//     {
//       name: 'Action',
//       width: "90px",
//       omit: !can('orders.change_status'),
//       cell: (row) => (
//         <div className='d-flex justify-content-end align-items-center' style={{ marginRight: '20px' }}>
//           <div className='cursor-pointer'>
//             <UncontrolledDropdown className='action_dropdown'>
//               <UncontrolledDropdownToggle className='action_btn'>
//                 <MoreVertical color='#000' size={16} />
//               </UncontrolledDropdownToggle>
//               <UncontrolledDropdownMenu>
//                 <UncontrolledDropdownItem onClick={() => handleNavigate(row?._id)}>
//                   View
//                   <FaRegEye />
//                 </UncontrolledDropdownItem>
//                 {can('orders.change_status') && <UncontrolledDropdownItem onClick={() => {
//                   toggleModal()
//                   setOrderId(row?._id)
//                   setStatusValue(row?.order_status)
//                 }}>
//                   Change Status
//                   <i className="fa fa-refresh" aria-hidden="true"></i>
//                 </UncontrolledDropdownItem>}
//                 {can('orders.change_status') && (row.order_status !== 'cancelled') &&
//                   <UncontrolledDropdownItem onClick={() => {
//                     toggleCancelOrderModal()
//                     setOrderId(row?._id)
//                     setStatusValue("cancelled")
//                   }}>
//                     Cancel Order
//                     <i className="fa fa-refresh" aria-hidden="true"></i>
//                   </UncontrolledDropdownItem>}
//                 {can('orders.change_status') && (row?.transaction_status === 'pending') &&
//                   <UncontrolledDropdownItem onClick={() => handlePaymentSuccess(row)}>
//                     Payment Success
//                     <i className="fa fa-check-circle" aria-hidden="true"></i>
//                   </UncontrolledDropdownItem>
//                 }
//                 <UncontrolledDropdownItem onClick={() => downloadInvoice(row?._id)}>
//                   Invoice
//                   <FaDownload />
//                 </UncontrolledDropdownItem>
//               </UncontrolledDropdownMenu>
//             </UncontrolledDropdown>
//           </div>
//         </div>
//       ),
//       sortable: false,
//       center: true,
//     },
//   ];

//   return (
//     <Fragment>
//       {/* ============= HEADER ROW - ORDERS TITLE ============= */}
//       <Row xxl={12} className="p-3">
//         <Col md={12}>
//           <div className="file-content align-items-center justify-content-between">
//             {/* <H5 attrH5={{ className: "mb-0 font-600-16" }}>Orders</H5> */}
//             <h5 className="fw-bolder">Orders</h5>
//             {/* RIGHT SIDE - Export Button */}
//             {can('orders.export') && <Button
//               className='btn btn-primary d-flex align-items-center'
//               onClick={exportAllOrders}
//               style={{ padding: '8px 18px', gap: '8px', whiteSpace: 'nowrap' }}
//               title="Export all orders to CSV"
//             >
//               <FaDownload size={16} />
//               <span>Export</span>
//             </Button>}
//           </div>
//         </Col>
//       </Row>

//       {/* ============= FILTERS & SEARCH ROW ============= */}
//       <Row xxl={12} className="mx-3 p-3 mb-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
//         <Col md={9} lg={9}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>

//             {/* LEFT SIDE - Date Range & Dropdowns */}
//             <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>

//               {/* Date Range Filters */}
//               <div className="d-flex align-items-center" style={{ gap: '8px' }}>
//                 <Label className="mb-0" style={{ whiteSpace: 'nowrap', fontWeight: '600', fontSize: '12px' }}>From:</Label>
//                 <Input
//                   type="date"
//                   value={startDate}
//                   onChange={handleStartDateChange}
//                   max={moment().format('YYYY-MM-DD')}
//                   style={{ width: '120px', padding: '6px 10px' , fontSize: '12px' }}
//                 />
//               </div>

//               <div className="d-flex align-items-center" style={{ gap: '8px' }}>
//                 <Label className="mb-0" style={{ whiteSpace: 'nowrap', fontWeight: '600', fontSize: '12px' }}>To:</Label>
//                 <Input
//                   type="date"
//                   value={endDate}
//                   onChange={handleEndDateChange}
//                   min={startDate}
//                   max={moment().format('YYYY-MM-DD')}
//                   style={{ width: '120px', padding: '6px 10px', fontSize: '12px'}}
//                 />
//               </div>

//               {/* Order Status Dropdown */}
//               <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
//                 <DropdownToggle caret style={{ minWidth: '140px', padding: '8px 8px', fontSize: '11px' }}>
//                   Status: {statusFilter === 'all' ? 'All' : capitalizeFirstLetter(statusFilter.replace(/-/g, ' '))}
//                 </DropdownToggle>
//                 <DropdownMenu style={{fontSize: '12px'}}>
//                   <DropdownItem onClick={() => handleStatusFilterChange('all')}>All</DropdownItem>
//                   <DropdownItem onClick={() => handleStatusFilterChange('accepted')}>Accepted</DropdownItem>
//                   <DropdownItem onClick={() => handleStatusFilterChange('pending')}>Pending</DropdownItem>
//                   <DropdownItem onClick={() => handleStatusFilterChange('on-the-way')}>On The Way</DropdownItem>
//                   <DropdownItem onClick={() => handleStatusFilterChange('delivered')}>Delivered</DropdownItem>
//                   <DropdownItem onClick={() => handleStatusFilterChange('cancelled')}>Cancelled</DropdownItem>
//                 </DropdownMenu>
//               </Dropdown>

//               {/* Payment Status Dropdown */}
//               <Dropdown isOpen={paymentDropdownOpen} toggle={togglePaymentDropdown}>
//                 <DropdownToggle caret style={{ minWidth: '160px', padding: '8px 8px', fontSize: '11px' }}>
//                   Payment: {paymentStatusFilter === 'all' ? 'All' : capitalizeFirstLetter(paymentStatusFilter)}
//                 </DropdownToggle>
//                 <DropdownMenu style={{fontSize: '12px'}}>
//                   <DropdownItem onClick={() => handlePaymentStatusFilterChange('all')}>All</DropdownItem>
//                   <DropdownItem onClick={() => handlePaymentStatusFilterChange('pending')}>Pending</DropdownItem>
//                   <DropdownItem onClick={() => handlePaymentStatusFilterChange('completed')}>Completed</DropdownItem>
//                 </DropdownMenu>
//               </Dropdown>
//             </div>


//           </div>
//         </Col>

//         <Col md={3} lg={3}>
//           <div className="form-group position-relative search_outer d-flex align-items-center">
//             <i className="fa fa-search"></i>
//             <input
//               className="form-control border-0"
//               defaultValue={searchTerm}
//               onChange={(e) => handleSearch(e)}
//               type="text"
//               placeholder="Search..."
//               style={{ paddingLeft: '35px' }}
//             />
//           </div>
//         </Col>
//       </Row>

//       {/* ============= DATA TABLE ============= */}
//       <Row xxl={12} className="p-3">
//         <Col md={12}>
//           <DataTable
//             data={orderData}
//             columns={orderColumns}
//             conditionalRowStyles={[
//               {
//                 when: (row) => row?.isScheduled === true,
//                 style: {
//                   backgroundColor: "#faf5ff",
//                   borderLeft: "3px solid #7c3aed",
//                 },
//               },
//             ]}
//             pagination
//             paginationServer
//             paginationTotalRows={totalRows}
//             paginationDefaultPage={currentPage}
//             paginationPerPage={perPage}
//             paginationRowsPerPageOptions={[10, 25, 50, 100]}
//             onChangeRowsPerPage={handlePerRowsChange}
//             onChangePage={handlePageChange}
//             onSelectedRowsChange={handleRowSelected}
//             clearSelectedRows={toggleDelet}
//             progressPending={isLoading}
//             progressComponent={<Loader />}
//           />
//         </Col>
//       </Row>

//       <CommonModal isOpen={showOrderCancellationPopup} title={"Cancel Order"} className="store_modal" toggler={toggleCancelOrderModal} size="md">
//         <Container>
//           <form className="p-2 md:p-4" onSubmit={formik.handleSubmit}>
//             <div className="flex flex-col gap-4">
//               <label
//                 style={{
//                   fontFamily: 'Lato',
//                   fontSize: '18px',
//                   fontWeight: '400',
//                   textAlign: 'left',
//                 }}
//               >
//                 Please enter the reason for cancellation
//               </label>
//               <input
//                 type="text"
//                 id="reason"
//                 name='cancel_reason'
//                 placeholder="Enter reason for cancellation"
//                 className={`w-[100%] focus:outline-none border-b-2 p-2`}
//                 onChange={formik.handleChange}
//                 onBlur={formik.handleBlur}
//                 value={formik.values.cancel_reason}
//               />
//               {formik.touched.cancel_reason && formik.errors.cancel_reason ? (
//                 <span className="text-red-500 text-sm mt-2">{formik.errors.cancel_reason}</span>
//               ) : (
//                 ""
//               )}
//             </div>
//             <div className="flex justify-center gap-4">
//               <button
//                 className="bg-[#D3178A] mt-4 w-32 h-12 rounded-full text-white"
//                 type="submit"
//               >
//                 Cancel Order
//               </button>
//             </div>
//           </form>
//         </Container>
//       </CommonModal>

//       <CommonModal
//         isOpen={showModal}
//         title={"Change Status"}
//         className="store_modal"
//         toggler={toggleModal}
//         size="md"
//       >
//         <Container>
//           <Form className="status_form" onSubmit={handleStatus}>
//             <>
//               <Col lg={12}>
//                 <FormGroup>
//                   <Row>
//                     <Col>
//                       <Label>
//                         <Input
//                           type="radio"
//                           name="status"
//                           className="me-2"
//                           id="1"
//                           value={"accepted"}
//                           checked={statusValue === "accepted"}
//                           onChange={(e) => setStatusValue(e.target.value)}
//                         />
//                         Accepted
//                       </Label>
//                     </Col>
//                     <Col>
//                       <Label>
//                         <Input
//                           type="radio"
//                           name="status"
//                           className="me-2"
//                           value="delivered"
//                           id="1"
//                           checked={statusValue === "delivered"}
//                           onChange={(e) => setStatusValue(e.target.value)}
//                         />
//                         Delivered
//                       </Label>
//                     </Col>
//                   </Row>
//                 </FormGroup>
//               </Col>
//             </>
//             <Row>
//               <Col xxl={12} className="text-right mt-3">
//                 <Button
//                   type="button"
//                   className="cancel_Btn"
//                   onClick={() => SetShowmodal(false)}
//                 >
//                   Cancel
//                 </Button>
//                 <Button
//                   type="submit"
//                   className="cursor-pointer bg-[#ff0000] font-medium w-40 ms-2 px-2 py-2 rounded-2xl text-white flex justify-center items-center"
//                 >
//                   Change Status
//                 </Button>
//               </Col>
//             </Row>
//           </Form>
//         </Container>
//       </CommonModal>
//     </Fragment>
//   );
// };

// export default DataTableComponent;